import { BACKEND_URL } from "@/lib/api";

const COINBASE_WS = "wss://ws-feed.exchange.coinbase.com";
const DIRECT_TIMEOUT = 3500;

let productsPromise = null;
export const validProducts = () => {
  if (!productsPromise) {
    productsPromise = fetch(`${BACKEND_URL}/api/live/products`)
      .then((r) => r.json())
      .then((ids) => new Set(ids))
      .catch(() => null);
  }
  return productsPromise;
};

// Streams ticker events {sym, price, bid, ask, open24h, size} for products like ["BTC-USD"].
// Tries the exchange WebSocket directly; if no tick arrives in time (blocked network/region),
// transparently switches to the backend relay (SSE over our own domain).
// onStatus(source) → "coinbase" | "relay" | "unavailable".
// onHealth({connected, lastMsgAt}) → feed liveness (heartbeat channel on BTC-USD ticks every second upstream).
// Returned handle: { close(), setProducts(list) } — setProducts re-subscribes without tearing down the direct socket.
export function openTickerStream(initialProducts, onTick, onStatus, onHealth) {
  let closed = false, ws = null, es = null, gotTick = false, timer = null, products = [...initialProducts], relayTimer = null;
  const health = (connected) => onHealth?.({ connected, lastMsgAt: Date.now() });

  const startRelay = () => {
    if (closed) return;
    if (ws) { ws.onclose = null; ws.onmessage = null; try { ws.close(); } catch (e) { console.warn("[apex] ignored:", e); } ws = null; }
    if (es) { es.close(); es = null; }
    if (!products.length) return;
    const syms = products.map((p) => p.split("-")[0].toLowerCase()).join(",");
    es = new EventSource(`${BACKEND_URL}/api/live/stream?symbols=${encodeURIComponent(syms)}`);
    es.addEventListener("tick", (ev) => {
      const m = JSON.parse(ev.data);
      if (!gotTick) { gotTick = true; onStatus?.("relay"); }
      health(true);
      onTick(m.sym, m.price, { bid: m.bid, ask: m.ask, open24h: m.open24h, size: m.size });
    });
    es.addEventListener("status", (ev) => {
      const h = JSON.parse(ev.data);
      onHealth?.({ connected: !!h.connected, lastMsgAt: h.connected ? Date.now() : h.last_msg_at });
    });
    es.onerror = () => { onHealth?.({ connected: false, lastMsgAt: null }); if (!gotTick && !closed) onStatus?.("unavailable"); };
  };

  const subscribe = (ids, type = "subscribe") => {
    if (ws && ws.readyState === 1 && ids.length) ws.send(JSON.stringify({ type, product_ids: ids, channels: ["ticker"] }));
  };

  const startDirect = () => {
    try {
      ws = new WebSocket(COINBASE_WS);
    } catch { startRelay(); return; }
    timer = setTimeout(() => { if (!gotTick) startRelay(); }, DIRECT_TIMEOUT);
    ws.onopen = () => {
      subscribe(products);
      ws.send(JSON.stringify({ type: "subscribe", product_ids: ["BTC-USD"], channels: ["heartbeat"] }));
    };
    ws.onmessage = (ev) => {
      const m = JSON.parse(ev.data);
      if (m.type === "ticker" && m.price) {
        if (!gotTick) { gotTick = true; clearTimeout(timer); onStatus?.("coinbase"); }
        health(true);
        onTick(m.product_id.split("-")[0], parseFloat(m.price), { bid: parseFloat(m.best_bid), ask: parseFloat(m.best_ask), open24h: parseFloat(m.open_24h), size: parseFloat(m.last_size) });
      } else if (m.type === "heartbeat" || m.type === "subscriptions") {
        if (!gotTick && m.type === "heartbeat") { gotTick = true; clearTimeout(timer); onStatus?.("coinbase"); }
        health(true);
      } else if (m.type === "error") {
        startRelay();
      }
    };
    ws.onerror = () => { if (!gotTick) startRelay(); };
    ws.onclose = () => { onHealth?.({ connected: false, lastMsgAt: null }); if (!closed && !es) setTimeout(() => { if (!closed) startDirect(); }, 3000); };
  };

  startDirect();
  return {
    close() {
      closed = true;
      clearTimeout(timer); clearTimeout(relayTimer);
      if (ws) { ws.onclose = null; try { ws.close(); } catch (e) { console.warn("[apex] ignored:", e); } }
      if (es) es.close();
    },
    setProducts(next) {
      const cur = new Set(products), nxt = new Set(next);
      const add = next.filter((p) => !cur.has(p)), remove = products.filter((p) => !nxt.has(p));
      products = [...next];
      if (!add.length && !remove.length) return;
      if (es) { clearTimeout(relayTimer); relayTimer = setTimeout(startRelay, 400); return; } // SSE is per-set: debounce a reconnect
      subscribe(add); subscribe(remove, "unsubscribe");
    },
  };
}

// Streams one symbol from the server-side aggregated feed (Coinbase + Kraken + KuCoin) over public SSE.
// onTick(sym, price, {source, at}); onStatus(sourceName | "unavailable"); onHealth({connected, lastMsgAt}).
export function openQuoteStream(symbol, onTick, onStatus, onHealth) {
  const sym = symbol.toUpperCase();
  let es = null, closed = false, source = null, gotAny = false;
  const start = () => {
    if (closed) return;
    es = new EventSource(`${BACKEND_URL}/api/market/stream?symbols=${encodeURIComponent(sym.toLowerCase())}`);
    const tick = (t) => {
      if (t.source !== source) { source = t.source; onStatus?.(source); }
      gotAny = true;
      onHealth?.({ connected: true, lastMsgAt: Date.now() });
      onTick(sym, t.price, { source: t.source, at: t.at, open24h: t.change24 != null ? t.price / (1 + t.change24 / 100) : undefined });
    };
    es.addEventListener("snapshot", (ev) => { const d = JSON.parse(ev.data); const q = d.quotes?.[sym]; if (q) tick(q); else if (!Object.keys(d.quotes || {}).length) onStatus?.("unavailable"); });
    es.addEventListener("ticks", (ev) => JSON.parse(ev.data).ticks.forEach((t) => { if (t.sym === sym) tick(t); }));
    es.addEventListener("status", (ev) => {
      const st = JSON.parse(ev.data); const s = source && st.sources?.[source];
      onHealth?.({ connected: !!(s && s.connected && s.last_msg_at && Date.now() - s.last_msg_at < 20000), lastMsgAt: s?.last_msg_at || null });
    });
    es.onerror = () => { onHealth?.({ connected: false, lastMsgAt: null }); if (!gotAny) onStatus?.("unavailable"); };
  };
  start();
  return { close() { closed = true; es?.close(); }, setProducts() {} };
}

// Streams order-book snapshots {bids:[[p,s]], asks:[[p,s]]} for one product via the backend relay
// (level2 snapshots are ~1MB on the raw socket, so the relay aggregates server-side for everyone).
export function openBookStream(symbol, depth, onBook, onStatus) {
  const es = new EventSource(`${BACKEND_URL}/api/live/book/${encodeURIComponent(symbol.toLowerCase())}?depth=${depth}`);
  es.addEventListener("book", (ev) => { onStatus?.("live"); onBook(JSON.parse(ev.data)); });
  es.addEventListener("unavailable", () => { onStatus?.("unavailable"); es.close(); });
  es.onerror = () => onStatus?.("connecting");
  return () => es.close();
}
